import { useEffect, useState } from 'react'

type AlertProps = {
  type: 'success' | 'error' | 'info' | 'warning'
  message: string
  onDismiss?: () => void
  autoDismiss?: boolean
  duration?: number
}

export default function Alert({
  type,
  message,
  onDismiss,
  autoDismiss = true,
  duration = 4000,
}: AlertProps) {
  const [isExiting, setIsExiting] = useState(false)

  useEffect(() => {
    if (!autoDismiss || !onDismiss) return

    const exitTimer = setTimeout(() => {
      setIsExiting(true)
    }, duration - 300) // Start exit animation 300ms before dismiss

    const dismissTimer = setTimeout(() => {
      onDismiss()
    }, duration)

    return () => {
      clearTimeout(exitTimer)
      clearTimeout(dismissTimer)
    }
  }, [autoDismiss, duration, onDismiss])

  const styles = {
    success:
      'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 border-green-100 dark:border-green-800/30',
    error:
      'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border-red-100 dark:border-red-800/30',
    info: 'bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 border-blue-100 dark:border-blue-800/30',
    warning:
      'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200 border-yellow-100 dark:border-yellow-800/30',
  }

  return (
    <div
      className={`p-3 rounded-lg text-sm border ${styles[type]} ${
        isExiting ? 'ds-alert-exit' : 'ds-alert-enter'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <span className="flex-1">{message}</span>
        {onDismiss && (
          <button
            type="button"
            onClick={() => {
              setIsExiting(true)
              setTimeout(onDismiss, 250)
            }}
            className="flex-shrink-0 opacity-70 hover:opacity-100 transition-opacity"
            aria-label="Dismiss"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  )
}
